import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import SEO from "@/components/SEO";
import { Loader2, AlertTriangle, Activity } from "lucide-react";
import { HEIRWISE_ENGINE_NAME, PROGRESSION_LAWS } from "@/lib/learning-engine/engineRules";

/**
 * /admin/learning-engine — read-only ops surface for the HeirWise
 * Adaptive Learning Engine. Renders the aggregate rollup returned by
 * the `get_learning_engine_rollup` RPC (admin-only via `is_admin()` in
 * the RPC body). Route protection follows the existing app pattern:
 * `ProtectedRoute` + `RoleGuard kind="admin_only"` in App.tsx.
 *
 * NOTE: The RPC itself enforces admin-only access server-side. Route
 * protection is defense-in-depth, not the primary guard.
 */
export default function AdminLearningEngine() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin-learning-engine-rollup"],
    queryFn: async () => {
      const { data, error } = await (supabase).rpc("get_learning_engine_rollup");
      if (error) throw error;
      return data as Record<string, unknown> | null;
    },
    staleTime: 30_000,
  });

  const rollup = (data ?? {}) as Record<string, unknown>;

  return (
    <div className="min-h-screen bg-background p-6 sm:p-10">
      <SEO
        title="Learning Engine — Admin"
        description="Aggregate rollup of the HeirWise Adaptive Learning Engine: decisions, quiz outcomes, retakes, and voice support."
        path="/admin/learning-engine"
      />
      <div className="max-w-5xl mx-auto space-y-8">
        <header className="space-y-2">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
            <Activity className="h-3.5 w-3.5" /> Ops
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
            {HEIRWISE_ENGINE_NAME}
          </h1>
          <p className="text-sm text-muted-foreground">
            Aggregate, non-PII rollup used to verify progression health during beta.
          </p>
        </header>

        <section className="rounded-2xl border border-border bg-card p-6">
          <h2 className="font-heading text-lg font-semibold mb-3">Live Rollup</h2>
          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading rollup…
            </div>
          )}
          {isError && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <div className="font-medium">Unable to load rollup.</div>
                <div className="text-xs opacity-80">
                  {(error as Error)?.message ?? "The engine rollup RPC returned an error."}
                </div>
              </div>
            </div>
          )}
          {!isLoading && !isError && (
            <pre className="text-xs bg-muted/40 rounded-md p-4 overflow-auto max-h-[60vh]">
              {JSON.stringify(rollup, null, 2)}
            </pre>
          )}
        </section>

        <section className="rounded-2xl border border-border bg-card p-6">
          <h2 className="font-heading text-lg font-semibold mb-3">Progression Laws</h2>
          <ul className="space-y-2 text-sm text-foreground/80 list-disc pl-5">
            {PROGRESSION_LAWS.map((law) => (
              <li key={law}>{law}</li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}